import { json } from 'd3-fetch';
import { hierarchy, pack } from 'd3-hierarchy';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { select } from 'd3-selection';
import Handlebars from 'handlebars';

import '../../scss/pages/mediamic-viz.scss';
import { registerHandlebarsHelpers } from '../modules/handlebars-helpers';

registerHandlebarsHelpers(Handlebars);

const width = 800;
const height = 650;

function uniqueValues(values) {
    return ['All', ...[...new Set(values)].sort()];
}

function packLeaves(children, valueAccessor) {
    const root = hierarchy({ children })
        .sum(valueAccessor)
        .sort((first, second) => second.value - first.value);

    return pack().size([width, height]).padding(3)(root).leaves();
}

function initMediamic(apiUrl) {
    const color = scaleOrdinal(schemeCategory10);

    const svg = select('#content')
        .append('svg')
        .attr('width', '100%')
        .attr('height', height)
        .attr('class', 'bubble');

    const tooltip = select('body')
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0);

    json(apiUrl).then((data) => {
        const cells = data.cells ?? [];
        const genders = uniqueValues(cells.map((cell) => cell.gender).filter(Boolean));
        const races = uniqueValues(cells.map((cell) => cell.race).filter(Boolean));
        const affiliationGroups = uniqueValues(cells.map((cell) => cell.affiliation_group).filter(Boolean));

        const filters = [
            { source_name: 'Gender', key: 'gender', values: genders },
            { source_name: 'Race', key: 'race', values: races },
            { source_name: 'Group', key: 'affiliation_group', values: affiliationGroups },
        ];

        const filterValues = { gender: 'All', race: 'All', affiliation_group: 'All' };
        const leaves = packLeaves(cells, (cell) => cell.record_count);

        const node = svg
            .selectAll('.node')
            .data(leaves)
            .enter()
            .append('g')
            .attr('class', 'node')
            .attr('transform', (datum) => `translate(${datum.x},${datum.y})`);

        node
            .append('circle')
            .attr('r', (datum) => datum.r)
            .style('fill', (datum) => color(affiliationGroups.indexOf(datum.data.affiliation_group)))
            .attr('data-name', (datum) => datum.data.name)
            .on('mouseover', () => {
                tooltip.style('opacity', 0.9);
            })
            .on('mousemove', (event, datum) => {
                let label = datum.data.source_name;

                if (datum.data.affiliation) {
                    label += `<br>${datum.data.affiliation}`;
                }

                if (datum.data.race) {
                    label += `<br>${datum.data.race}`;
                }

                if (datum.data.gender) {
                    label += `<br>${datum.data.gender}`;
                }

                label += "<br clear='both'>";

                tooltip
                    .html(label)
                    .style('top', `${event.pageY - 15}px`)
                    .style('left', `${event.pageX + 10}px`);
            })
            .on('mouseout', () => {
                tooltip.style('opacity', 0);
            });

        node
            .append('text')
            .attr('dy', '.3em')
            .style('text-anchor', 'middle')
            .text((datum) => {
                if (datum.data.record_count > 10) {
                    return datum.data.source_name.split(' ').slice(-1);
                }

                return '';
            });

        const selects = select('#selects')
            .selectAll('select')
            .data(filters)
            .enter()
            .append('div')
            .classed('col-lg-4', true);

        selects
            .append('label')
            .classed('form-label', true)
            .text((datum) => datum.source_name);

        selects
            .append('select')
            .classed('form-select', true)
            .classed('select_filter', true)
            .attr('id', (datum) => `select_${datum.key}`)
            .attr('data-key', (datum) => datum.key)
            .on('change', function onFilterChange() {
                const selectElement = select(this);
                filterValues[selectElement.attr('data-key')] = selectElement.property('value');
                applyFilters();
            })
            .selectAll('option')
            .data((datum) => datum.values)
            .enter()
            .append('option')
            .attr('value', (datum) => datum)
            .text((datum) => (datum === '' ? 'Unknown' : datum));

        document.getElementById('newsmakersloader')?.style.setProperty('display', 'none');

        function applyFilters() {
            svg.selectAll('.node').classed('hidden', (datum) => {
                let display = true;

                Object.keys(filterValues).forEach((key) => {
                    const filter = filterValues[key];

                    if (filter === 'All') {
                        display = display || false;
                    } else if (datum.data[key] !== filter) {
                        display = display && false;
                    }
                });

                return !display;
            });
        }
    });
}

function bindDateRangeForm() {
    const startSelect = document.getElementById('select_start_date');
    const endSelect = document.getElementById('select_end_date');
    const button = document.querySelector('[data-mediamic-date-submit]');

    if (!startSelect || !endSelect || !button) {
        return;
    }

    button.addEventListener('click', () => {
        const start = startSelect.value;
        const end = endSelect.value;
        const baseUrl = window.location.href.split(/[?#]/)[0];

        window.location.href = `${baseUrl}?start_date=${start}&end_date=${end}`;
    });
}

function boot() {
    const root = document.getElementById('mediamic-root');

    if (!root) {
        return;
    }

    bindDateRangeForm();
    initMediamic(root.dataset.apiUrl);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}

window.initMediamic = initMediamic;
